import { useBusinessContext } from "../BusinessContext";
import { personScopeUrl } from "../components/personScope";
import { formatPersonName } from "../components/personName";
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import TaskAlt from "@mui/icons-material/TaskAlt";
import dayjs from "dayjs";
import api from "../api/axios";
import BestellungDialog from "../components/BestellungDialog";
import RecordLink from "../components/RecordLink";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';


const Bestellungen = () => {
  const { mandantId, betriebId } = useBusinessContext();
  const generation = useRef(0);
  const scoped = useCallback(url => personScopeUrl(url, mandantId, betriebId), [mandantId, betriebId]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [error, setError] = useState("");
  const [completingIds, setCompletingIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const loadOrders = useCallback(() => {
    if (!mandantId || !betriebId) return;
    const current = generation.current;
    setLoading(true); setError('');
    api.get(scoped("/bestellungen?offen=true"))
      .then(response => { if (generation.current === current) setOrders(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (generation.current === current) setError("Bestellungen konnten nicht geladen werden."); })
      .finally(() => { if (generation.current === current) setLoading(false); });
  }, [mandantId, betriebId, scoped]);

  useEffect(() => {
    const current = ++generation.current;
    setOrders([]); setCustomers([]); setSelectedOrder(null); setDialogOpen(false); setError(""); setCompletingIds([]); setLoading(false); setCustomersLoading(false);
    if (mandantId && betriebId) {
      loadOrders();
      setCustomersLoading(true);
      api.get(scoped("/personen/kundenzuordnungen"))
        .then(response => { if (generation.current === current) setCustomers((Array.isArray(response.data) ? response.data : []).filter(customer => Boolean(customer.Aktiv))); })
        .catch(() => { if (generation.current === current) setError("Kunden konnten nicht geladen werden."); })
        .finally(() => { if (generation.current === current) setCustomersLoading(false); });
    }
    return () => { generation.current = current + 1; };
  }, [loadOrders, mandantId, betriebId, scoped, attempt]);

  const openDialog = (order = null) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const completeOrder = async (order) => {
    if (!window.confirm("Bestellung wirklich als erledigt markieren?")) return;
    const current = generation.current;
    setCompletingIds((ids) => [...ids, order.Id]);
    try {
      await api.put(scoped(`/bestellungen/${order.Id}`), { Erledigt: true });
      if (generation.current !== current) return;
      setOrders((current) => current.filter((entry) => entry.Id !== order.Id));
    } catch (requestError) {
      if (generation.current !== current) return;
      setError(requestError.response?.data?.error || "Bestellung konnte nicht als erledigt markiert werden.");
    } finally {
      if (generation.current === current) setCompletingIds((ids) => ids.filter((id) => id !== order.Id));
    }
  };

  return (
    <Box>
      <PageHeader title="Offene Bestellungen" action={<Button variant="contained" size="small" startIcon={<Add />} disabled={!mandantId || !betriebId} onClick={() => openDialog()}>
          Neu
        </Button>} />
      {(!mandantId || !betriebId) && <Alert severity="info">Bitte Mandant und Betrieb auswählen.</Alert>}
      {loading && <LoadingIndicator label="Bestellungen werden geladen …" />}
      {customersLoading && <LoadingIndicator label="Kunden werden geladen …" compact />}
      {error && <LoadError onRetry={() => setAttempt(value => value + 1)}>{error}</LoadError>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kunde</TableCell>
              <TableCell>Bestelldatum</TableCell>
              <TableCell>Bestellung</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && !error && mandantId && betriebId && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Keine offenen Bestellungen.</TableCell>
              </TableRow>
            ) : orders.map((order) => (
              <TableRow key={order.Id} hover>
                <TableCell><RecordLink page="kunden" id={order.person_id}>{formatPersonName(order.Kunde_Name, order.Kunde_Vorname)}</RecordLink></TableCell>
                <TableCell>
                  {order.Bestelldatum ? dayjs(order.Bestelldatum).format("DD.MM.YYYY") : ""}
                </TableCell>
                <TableCell>{order.Bestellung}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Bestellung bearbeiten">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Bestellung ${order.Id} bearbeiten`}
                      onClick={() => openDialog(order)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Als erledigt markieren">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Als erledigt markieren"
                        disabled={completingIds.includes(order.Id)}
                        onClick={() => completeOrder(order)}
                      >
                        <TaskAlt fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BestellungDialog
        open={dialogOpen}
        order={selectedOrder}
        customers={customers}
        loading={customersLoading}
        onClose={() => {
          setDialogOpen(false);
          setSelectedOrder(null);
        }}
        onSaved={loadOrders}
      />
    </Box>
  );
};

export default Bestellungen;
